import { useDirection } from '@creditornot/cb-provider';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import styled from 'styled-components';

import ScientistError from 'common/illustrations/ScientistError';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import { ListRowRenderer, ListRowRendererProps } from './ListRowRenderer';
import { useVirtualListHeights, getItemKey } from '../../../SharedMenuEditorPage/components/Collections/utils';
import { SharedMenuElement } from '../../../SharedMenuEditorPage/types';
import { OfferDraftChanges } from '../../useOfferDraftChanges';
import { CenteredEmpty } from '../CenteredEmpty';

type Props = {
  menuEditorList: SharedMenuElement[];
  offerDraftChanges: OfferDraftChanges;
  onDetachOfferImage: (offerId: string, deleteAt: number) => Promise<void>;
};

export const StyledScientistError = styled(ScientistError)`
  width: 100%;
  max-width: 375px;
  margin-block-end: 32px;
`;

export const OffersListView = ({ menuEditorList, offerDraftChanges, onDetachOfferImage }: Props) => {
  const { selection } = useSelectionData();
  const cbDirection = useDirection();
  const intl = useIntl();
  const { listRef, virtualListHeights } = useVirtualListHeights(menuEditorList);
  const primaryLanguage = selection.primary_language;

  if (menuEditorList && !menuEditorList.length) {
    return (
      <CenteredEmpty
        image={<StyledScientistError />}
        description={<FormattedMessage id="shared-menu.editor.image-gallery.empty-state.start-building-menu" />}
        title={intl.formatMessage({ id: 'shared-menu.editor.image-gallery.empty-state.no-items-found' })}
      />
    );
  }

  return (
    <AutoSizer disableWidth>
      {({ height }) => (
        <VariableSizeList<ListRowRendererProps['data']>
          ref={listRef}
          style={{ paddingBlockEnd: 105, direction: cbDirection }}
          height={height}
          width="100%"
          itemData={{
            itemList: menuEditorList,
            primaryLanguage,
            offerDraftChanges,
            onDetachOfferImage,
          }}
          itemCount={menuEditorList.length}
          itemSize={index => virtualListHeights[index]}
          itemKey={(index, data) => getItemKey(index, data.itemList)}
          overscanCount={6}
        >
          {ListRowRenderer}
        </VariableSizeList>
      )}
    </AutoSizer>
  );
};
